import { useState, useCallback } from 'react';
import {
  securityService,
  IpWhitelistEntry,
  IpRestrictionSettings,
  CreateIpWhitelistDto,
  UpdateIpWhitelistDto,
  UpdateIpRestrictionSettingsDto,
  IpViolationLog,
  SamlConfiguration,
  CreateSamlConfigurationDto,
  UpdateSamlConfigurationDto,
  SsoSession,
} from '@services/security.service';

// ===== IP WHITELIST HOOKS =====

export function useIpRestrictionSettings() {
  const [settings, setSettings] = useState<IpRestrictionSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await securityService.getIpRestrictionSettings();
      setSettings(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch IP restriction settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (data: UpdateIpRestrictionSettingsDto) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await securityService.updateIpRestrictionSettings(data);
      setSettings(updated);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { settings, isLoading, error, fetchSettings, updateSettings };
}

export function useIpWhitelist() {
  const [whitelist, setWhitelist] = useState<IpWhitelistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWhitelist = useCallback(async (activeOnly = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await securityService.getIpWhitelist(activeOnly);
      setWhitelist(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch IP whitelist');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createEntry = useCallback(async (data: CreateIpWhitelistDto) => {
    setError(null);
    try {
      const entry = await securityService.createIpWhitelistEntry(data);
      setWhitelist((prev) => [entry, ...prev]);
      return entry;
    } catch (err: any) {
      setError(err.message || 'Failed to add IP');
      throw err;
    }
  }, []);

  const updateEntry = useCallback(async (id: string, data: UpdateIpWhitelistDto) => {
    setError(null);
    try {
      const updated = await securityService.updateIpWhitelistEntry(id, data);
      setWhitelist((prev) => prev.map((e) => (e.id === id ? updated : e)));
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update IP entry');
      throw err;
    }
  }, []);

  const deleteEntry = useCallback(async (id: string) => {
    setError(null);
    try {
      await securityService.deleteIpWhitelistEntry(id);
      setWhitelist((prev) => prev.filter((e) => e.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete IP entry');
      throw err;
    }
  }, []);

  const toggleEntry = useCallback(async (id: string, isActive: boolean) => {
    setError(null);
    try {
      const updated = await securityService.toggleIpWhitelistEntry(id, isActive);
      setWhitelist((prev) => prev.map((e) => (e.id === id ? updated : e)));
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to toggle IP entry');
      throw err;
    }
  }, []);

  return {
    whitelist,
    isLoading,
    error,
    fetchWhitelist,
    createEntry,
    updateEntry,
    deleteEntry,
    toggleEntry,
  };
}

export function useIpViolationLogs() {
  const [logs, setLogs] = useState<IpViolationLog[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async (options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await securityService.getIpViolationLogs(options);
      setLogs(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch violation logs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { logs, total, isLoading, error, fetchLogs };
}

// ===== SAML SSO HOOKS =====

export function useSamlConfiguration() {
  const [config, setConfig] = useState<SamlConfiguration | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfiguration = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await securityService.getSamlConfiguration();
      if ('configured' in data && !data.configured) {
        setIsConfigured(false);
        setConfig(null);
      } else {
        setIsConfigured(true);
        setConfig(data as SamlConfiguration);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch SAML configuration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createConfiguration = useCallback(async (data: CreateSamlConfigurationDto) => {
    setIsLoading(true);
    setError(null);
    try {
      const created = await securityService.createSamlConfiguration(data);
      setConfig(created);
      setIsConfigured(true);
      return created;
    } catch (err: any) {
      setError(err.message || 'Failed to create SAML configuration');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateConfiguration = useCallback(async (data: UpdateSamlConfigurationDto) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await securityService.updateSamlConfiguration(data);
      setConfig(updated);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update SAML configuration');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteConfiguration = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await securityService.deleteSamlConfiguration();
      setConfig(null);
      setIsConfigured(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete SAML configuration');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleEnabled = useCallback(async (enabled: boolean) => {
    setError(null);
    try {
      const updated = await securityService.toggleSamlEnabled(enabled);
      setConfig(updated);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to toggle SAML');
      throw err;
    }
  }, []);

  const generateKeyPair = useCallback(async () => {
    setError(null);
    try {
      const result = await securityService.generateSpKeyPair();
      // Refresh config to get updated certificate status
      await fetchConfiguration();
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to generate key pair');
      throw err;
    }
  }, [fetchConfiguration]);

  return {
    config,
    isConfigured,
    isLoading,
    error,
    fetchConfiguration,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    toggleEnabled,
    generateKeyPair,
  };
}

export function useSamlMetadata() {
  const [metadata, setMetadata] = useState<{
    entityId: string;
    acsUrl: string;
    metadataXml: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetadata = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await securityService.getSamlMetadata();
      setMetadata(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch SAML metadata');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { metadata, isLoading, error, fetchMetadata };
}

export function useSsoSessions() {
  const [sessions, setSessions] = useState<SsoSession[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async (options?: {
    userId?: string;
    activeOnly?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await securityService.getSsoSessions(options);
      setSessions(result.sessions);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch SSO sessions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const terminateSession = useCallback(async (sessionId: string) => {
    setError(null);
    try {
      await securityService.terminateSsoSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err: any) {
      setError(err.message || 'Failed to terminate session');
      throw err;
    }
  }, []);

  const terminateAllUserSessions = useCallback(async (userId: string) => {
    setError(null);
    try {
      await securityService.terminateAllUserSsoSessions(userId);
      setSessions((prev) => prev.filter((s) => s.userId !== userId));
    } catch (err: any) {
      setError(err.message || 'Failed to terminate user sessions');
      throw err;
    }
  }, []);

  return {
    sessions,
    total,
    isLoading,
    error,
    fetchSessions,
    terminateSession,
    terminateAllUserSessions,
  };
}
